import { CommonModule } from '@angular/common';
import { Component, ElementRef, Inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MaterialModule } from '../../material/material.module';
import { Video } from '../../models/video';
import { PipesModule } from '../../pipes/pipes.module';
import { LanguageSelectorComponent } from '../language-selector/language-selector.component';
import { MediaLibraryService } from '../../services/media-library/media-library.service';
import { AuthService } from '../../services/auth/auth.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SpinnerService } from '../../services/spinner/spinner.service';
import { ToasterService } from '../../services/toaster/toaster.service';
import { Table } from '../../models/table';
import { TableComponent } from '../table/table.component';
import { getFileNameExtension, isNoEmptyArray, isNullOrUndefined } from '@pineapplelab/util';
import { Overlay, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { OverlayComponent } from '../overlay/overlay.component';
import { ComponentPortal } from '@angular/cdk/portal';
import { Language } from '../../utils/language';
import { RoleEnum } from '../../enums/role.enum';
import { FormsModule } from '@angular/forms';
import firebase from "firebase/compat/app";
import { ToasterTypeEnum } from '../../enums/toasterType.enum';
import { FileUtils } from '../../utils/fileUtils';
import { firstValueFrom } from 'rxjs';
import { MediaDescriptionDialogComponent } from '../media-description-dialog/media-description-dialog.component';
import { MediaLibraryFile } from '../../enums/mediaLibraryFileType.enum';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-upload-video',
  standalone: true,
  imports: [CommonModule, MaterialModule, PipesModule, LanguageSelectorComponent, TableComponent, OverlayModule, FormsModule],
  templateUrl: './upload-video.component.html',
  styleUrl: './upload-video.component.scss'
})
export class UploadVideoComponent implements OnInit {

	@ViewChild('videoThumbnail', { static: true }) videoThumbnail: TemplateRef<any>;
	@ViewChild('language', { static: true }) language: TemplateRef<any>;
	@ViewChild('addLanguage', { static: true }) addLanguage: TemplateRef<any>;
	@ViewChild('videoDuration', { static: true }) videoDuration: TemplateRef<any>;
	@ViewChild('descriptionTemplate', { static: true }) descriptionTemplate: TemplateRef<any>;
	@ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;
	@ViewChild('fileInputMultiple') fileInputMultiple: ElementRef<HTMLInputElement>;
	@ViewChild(TableComponent) tableComponent: TableComponent;

	model: Video = new Video();
	hasFile: boolean = false;
	videos: Video[] = [];
	table: Table<Video>;
	hasTranslation: boolean = false;
	uploadOnCourse: boolean = false;
	private overlayRef: OverlayRef;
	acceptFiles = this.mediaLibraryService.getValidFilesToString();
	MediaFiles = MediaLibraryFile;
	pdfThumbnail = environment.pdfPlaceholderImage;

	constructor(
		private matDialogRef: MatDialogRef<UploadVideoComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any,
		private authService: AuthService,
		private mediaLibraryService: MediaLibraryService,
		private spinnerService: SpinnerService,
		private toasterService: ToasterService,
		private overlay: Overlay,
		private dialog: MatDialog
	) { }

	async ngOnInit(): Promise<void> {
		if(!isNullOrUndefined(this.data)){
			this.uploadOnCourse = this.data?.uploadOnCourse;
		}

	}
	ngAfterViewInit(): void {
		this.table = new Table({
			columns: [
				{ label: 'general.file_name', field: 'videoThumbnail', template: this.videoThumbnail, showOnlyTemplate: true},
				{ label: 'general.description', field: 'description', template: this.descriptionTemplate, showOnlyTemplate: true},
				{ label: 'general.language', field: 'language', template: this.addLanguage, showOnlyTemplate: true },
				{ label: 'general.duration', field: 'duration', template: this.videoDuration, showOnlyTemplate: true },
			],
			selectable: false,
			actions: true,
			type: 'none'
		});
	}

	openOverlay(event: MouseEvent, element: Video) {
		const positionStrategy = this.overlay.position()
			.flexibleConnectedTo(event.target as HTMLElement)
			.withPositions([
			{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
			]);

		this.overlayRef = this.overlay.create({
			positionStrategy,
			hasBackdrop: true,
		});

		const overlayPortal = new ComponentPortal(OverlayComponent);
		const overlayComponentRef = this.overlayRef.attach(overlayPortal);

		overlayComponentRef.instance.dataSelected.subscribe((selectedData: Language) => {
			this.addLanguageOnVideo(selectedData, element);
			this.overlayRef.dispose();
		});
		this.overlayRef.backdropClick().subscribe(() => {
			this.overlayRef.dispose();
		});
  	}

	addLanguageOnVideo(data: Language, element: Video) {
		element.language = data;
	}

	async save(){
		if (this.data?.lang) {
			this.addLanguageOnVideo(this.data.lang.translation, this.videos[0]);
		}
		if (this.videos.some(video => !video.language)){
			this.hasTranslation = true;
			return;
		}
		this.spinnerService.show();
		this.setCreatedByAdmin(this.videos);
		this.videos.forEach(video => {
			video.createdAt = firebase.firestore.FieldValue.serverTimestamp();
			video.nameLowercase = video?.fileName?.toLowerCase();
		});
		if(this.hasFile){
			this.videos.filter(media => media?.fileType === MediaLibraryFile.IMAGE).forEach(image => this.disposeDynamicURLFromImages(image));
			await this.mediaLibraryService.save(this.authService.projectId, this.videos);
			this.toasterService.show('general.we_are_processing_your_video', ToasterTypeEnum.SUCCESS)
		}
		this.matDialogRef.close(true);
		this.spinnerService.hide();
	}

	setCreatedByAdmin(videos: Video[]): void {
		if (!videos || !isNoEmptyArray(videos)) {
			return;
		}

		videos.forEach(video => video.createdByAdmin = this.authService.auth?.user?.role === RoleEnum.ADMIN);
	}

	deleteVideo(ev:any){
		this.videos = this.videos.filter(video => video.fileName !== ev.item.fileName);
		this.fileInput.nativeElement.value = '';
		this.fileInputMultiple.nativeElement.value = '';
		if(this.videos.length === 0){
			this.hasFile = false;
		}
	}

	getInvalidFileTypesMessage(invalidFiles: File[]): { key: string, params: { [key: string]: any } } {
		if (!isNoEmptyArray(invalidFiles)) {
			return null;
		}

		const fileTitles: string[] = invalidFiles.map(file => file?.name);
		return fileTitles.length > 1 ? { key: `general.invalid_files`, params: { item: fileTitles.join(', ') }} : { key: `general.invalid_file`, params: { item: fileTitles[0] }};
	}

	async fileChangeEvent(event: any) {
		let files = event.target.files;
		this.hasFile = true;
		files = Array.from(files);

		if (!this.mediaLibraryService.areAllFilesValid(files)) {
			const invalidFiles = files.filter((file: File) => !this.mediaLibraryService.isFileValid(file));
			const error = this.getInvalidFileTypesMessage(invalidFiles);
			this.toasterService.showError(error?.key, error?.params);
			files = files.filter((file: File) => this.mediaLibraryService.isFileValid(file));
			if (this.videos.length === 0) {
				this.hasFile = false;
			}
		}

		const newVideos = await this.mediaLibraryService.preprocessMediaFiles(files);
		this.videos.push(...newVideos);
		if (this.tableComponent) {
			this.tableComponent.rerender();
		}

	}

	async generateThumbnail(file: File): Promise<{ thumbnail: string, thumbnailFile: File | null, duration: number }> {
		return FileUtils.getVideoThumbnail(file);
	}

	close(){
		this.matDialogRef.close(true);
	}

	editFileTitle(element: Video): void {
		element.__previousFileName = element.fileName;
		element.__fileExtension = getFileNameExtension(element.__previousFileName);
		element.__editFileNameTitle = true;
	}

	maintainPreviousFileName(element: Video): void {
		element.fileName = element.__previousFileName;
		element.__editFileNameTitle = false;
	}

	confirmFileName(element: Video): void {
		element.__editFileNameTitle = false;
		if (!element?.fileName?.trim() || element?.fileName?.trim() === `.${element?.__fileExtension}`) {
			element.fileName = 'video';
		}
		if (element.fileName.endsWith(`.${element.__fileExtension}`)) {
			return;
		}
		element.fileName = `${element.fileName.trim()}.${element.__fileExtension}`;
	}

	submitNewName(event: KeyboardEvent, element: Video): void {
		if (event.key === 'Enter') {
			this.confirmFileName(element);
		}
	}

	async openDescriptionDialog(element: Video): Promise<void> {
		const result = await firstValueFrom(this.dialog.open(MediaDescriptionDialogComponent, {
			width: '30%',
			height: 'auto',
			maxHeight: '90vh',
			maxWidth: '90vw',
			data: element?.description
		}).afterClosed());

		element.description = result;
	}

	private disposeDynamicURLFromImages(media: Video): void {
		if (media?.fileType === MediaLibraryFile.IMAGE && media?.thumbnail?.startsWith('blob')) {
			URL.revokeObjectURL(media.thumbnail);
		}
	}
}
